import API from '../../config/api';
import React, { useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Row, Col, Card } from 'react-bootstrap';
/* Accent palette — one hue per card so a metric is recognisable at a glance.
   Retuned to temple pigments: kumkum, saffron, peacock, tulsi, turmeric,
   sindoor and sandalwood, so the board reads as a sanctum without losing
   the colour coding. */
const TONES = {
  violet: { c1: '#5b21b6', c2: '#a78bfa', soft: '#f6f2ff' },
  orange: { c1: '#9a3412', c2: '#f59e0b', soft: '#fff7ed' },
  blue: { c1: '#1e3a8a', c2: '#60a5fa', soft: '#eff4ff' },
  green: { c1: '#14532d', c2: '#22c55e', soft: '#f0fdf4' },
  amber: { c1: '#b45309', c2: '#fbbf24', soft: '#fffbeb' },
  red: { c1: '#9f1239', c2: '#fb7185', soft: '#fff1f2' },
  cyan: { c1: '#0e7490', c2: '#22d3ee', soft: '#ecfeff' },
  slate: { c1: '#57534e', c2: '#a8a29e', soft: '#faf7f2' }
};

/* Counts up to a number so the dashboard feels alive on load. Non-numeric
   values (e.g. the top buyer's name) are passed straight through. */
function useCountUp(target, active) {
  const numeric = typeof target === 'number' && Number.isFinite(target);

  const [display, setDisplay] = useState(numeric ? 0 : target);

  const frame = useRef(null);

  useEffect(() => {
    if (!numeric) {
      setDisplay(target);
      return undefined;
    }

    if (!active) {
      return undefined;
    }

    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;

    if (reduced || target === 0) {
      setDisplay(target);
      return undefined;
    }

    const duration = 900;

    let start = null;

    const step = (timestamp) => {
      if (start === null) {
        start = timestamp;
      }

      const progress = Math.min((timestamp - start) / duration, 1);

      /* easeOutCubic — fast then settling */
      const eased = 1 - Math.pow(1 - progress, 3);

      setDisplay(target * eased);

      if (progress < 1) {
        frame.current = requestAnimationFrame(step);
      }
    };

    frame.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame.current);
  }, [target, numeric, active]);

  return display;
}

/*
  One metric tile. `value` may be a number (animated, formatted) or a string.
  `share` (0–100) draws a proportion bar, `alert` flags a number that needs
  the admin's attention.
*/
function StatTile({
  icon,
  value,
  label,
  tone = 'orange',
  prefix = '',
  decimals = 0,
  share = null,
  alert = false,
  loading = false
}) {
  const animated = useCountUp(value, !loading);

  const palette = TONES[tone] || TONES.orange;

  const renderValue = () => {
    if (typeof value !== 'number') {
      return value || '—';
    }

    return `${prefix}${animated.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  };

  if (loading) {
    return (
      <Card className="border-0 h-100 sc-tile sc-tile-skel">
        <Card.Body className="p-3 p-md-4">
          <span className="sc-skel sc-skel-icon" />
          <span className="sc-skel sc-skel-value" />
          <span className="sc-skel sc-skel-label" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card
      className={`border-0 h-100 sc-tile${alert ? ' sc-tile-alert' : ''}`}
      style={{
        '--c1': palette.c1,
        '--c2': palette.c2,
        '--soft': palette.soft
      }}
    >
      <span className="sc-accent" />

      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span className="sc-icon">{icon}</span>

          {alert && <span className="sc-flag">Needs attention</span>}
        </div>

        <div className="sc-value" title={String(value)}>
          {renderValue()}
        </div>

        <div className="sc-label">{label}</div>

        {share !== null && (
          <div className="sc-bar" title={`${Math.round(share)}% of total`}>
            <span
              className="sc-bar-fill"
              style={{ width: `${Math.max(0, Math.min(100, share))}%` }}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/* Heading above each band of tiles */
function SectionHead({ icon, title, hint }) {
  return (
    <div className="sc-head">
      <span className="sc-head-icon">{icon}</span>

      <div>
        <h5 className="sc-head-title">{title}</h5>
        {hint && <small className="sc-head-hint">{hint}</small>}
      </div>

      <span className="sc-head-rule" />
    </div>
  );
}

/* All the dashboard summary stat cards, each fetching its own metrics. */
function StatsCards() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0
  });
  const [orderStatus, setOrderStatus] = useState({
    confirmed: 0,
    processing: 0,
    shipped: 0,
    outForDelivery: 0,
    delivered: 0,
    cancelled: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    todayRevenue: 0,
    weekRevenue: 0,
    monthRevenue: 0
  });
  const [inventoryStats, setInventoryStats] = useState({
    totalInventoryValue: 0,
    lowStockValue: 0,
    outOfStockProducts: 0
  });
  const [customerStats, setCustomerStats] = useState({
    totalCustomers: 0,
    newCustomers: 0,
    topBuyer: '',
    averageOrderValue: 0
  });
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    closed: 0
  });
  const [refundStats, setRefundStats] = useState({});
  const [reviewStats, setReviewStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    fiveStarReviews: 0,
    oneStarReviews: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async (url, setter) => {
      try {
        const { data } = await axios.get(url);
        setter(data);
      } catch (error) {
        console.log(error);
      }
    };

    Promise.all([
      load(`${API}/admin/stats`, setStats),
      load(`${API}/admin/order-status-stats`, setOrderStatus),
      load(`${API}/admin/revenue-stats`, setRevenueStats),
      load(`${API}/admin/inventory-analytics`, setInventoryStats),
      load(`${API}/admin/customer-analytics`, setCustomerStats),
      load(`${API}/tickets/stats`, setTicketStats),
      load(`${API}/refunds/stats`, setRefundStats),
      load(`${API}/admin/review-analytics`, setReviewStats)
    ]).finally(() => setLoading(false));
  }, []);

  /* Denominators for the proportion bars */
  const orderTotal =
    (orderStatus.confirmed || 0) +
    (orderStatus.processing || 0) +
    (orderStatus.shipped || 0) +
    (orderStatus.outForDelivery || 0) +
    (orderStatus.delivered || 0) +
    (orderStatus.cancelled || 0);

  /* The refunds endpoint reports its own total; fall back to the sum */
  const refundTotal =
    refundStats.total ||
    (refundStats.pending || 0) +
      (refundStats.approved || 0) +
      (refundStats.refunded || 0) +
      (refundStats.rejected || 0);

  const share = (value, total) => (total > 0 ? ((value || 0) / total) * 100 : 0);

  return (
    <>
      <style>{statCardStyles}</style>

      {/* ===== Overview ===== */}
      <SectionHead
        icon="📊"
        title="Business Overview"
        hint="Everything at a glance"
      />

      <Row className="g-3 mb-5">
        <Col xs={6} md={3}>
          <StatTile
            icon="👥"
            value={stats.totalUsers}
            label="Total Users"
            tone="violet"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="📦"
            value={stats.totalProducts}
            label="Total Products"
            tone="blue"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="📋"
            value={stats.totalOrders}
            label="Total Orders"
            tone="cyan"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="💰"
            value={stats.totalRevenue}
            label="Total Revenue"
            tone="green"
            prefix="₹"
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Order status ===== */}
      <SectionHead
        icon="🚚"
        title="Order Pipeline"
        hint={orderTotal > 0 ? `${orderTotal} orders tracked` : null}
      />

      <Row className="g-3 mb-5">
        <Col xs={6} md={4} lg>
          <StatTile
            icon="🧾"
            value={orderStatus.confirmed}
            label="Confirmed"
            tone="violet"
            share={share(orderStatus.confirmed, orderTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4} lg>
          <StatTile
            icon="⏳"
            value={orderStatus.processing}
            label="Processing"
            tone="amber"
            share={share(orderStatus.processing, orderTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4} lg>
          <StatTile
            icon="🚚"
            value={orderStatus.shipped}
            label="Shipped"
            tone="blue"
            share={share(orderStatus.shipped, orderTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4} lg>
          <StatTile
            icon="🏠"
            value={orderStatus.outForDelivery}
            label="Out For Delivery"
            tone="cyan"
            share={share(orderStatus.outForDelivery, orderTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={6} lg>
          <StatTile
            icon="✅"
            value={orderStatus.delivered}
            label="Delivered"
            tone="green"
            share={share(orderStatus.delivered, orderTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={6} lg>
          <StatTile
            icon="✖"
            value={orderStatus.cancelled}
            label="Cancelled"
            tone="red"
            share={share(orderStatus.cancelled, orderTotal)}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Revenue ===== */}
      <SectionHead icon="💹" title="Revenue" hint="Money in, by period" />

      <Row className="g-3 mb-5">
        <Col xs={12} md={4}>
          <StatTile
            icon="💵"
            value={revenueStats.todayRevenue}
            label="Today's Revenue"
            tone="green"
            prefix="₹"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            icon="📈"
            value={revenueStats.weekRevenue}
            label="This Week"
            tone="cyan"
            prefix="₹"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            icon="🏆"
            value={revenueStats.monthRevenue}
            label="This Month"
            tone="violet"
            prefix="₹"
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Inventory ===== */}
      <SectionHead
        icon="🏷"
        title="Inventory Health"
        hint="Stock value and gaps"
      />

      <Row className="g-3 mb-5">
        <Col xs={12} md={4}>
          <StatTile
            icon="📦"
            value={inventoryStats.totalInventoryValue}
            label="Total Inventory Value"
            tone="blue"
            prefix="₹"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            icon="⚠️"
            value={inventoryStats.lowStockValue}
            label="Low Stock Value"
            tone="amber"
            prefix="₹"
            alert={inventoryStats.lowStockValue > 0}
            loading={loading}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            icon="❌"
            value={inventoryStats.outOfStockProducts}
            label="Out Of Stock Products"
            tone="red"
            alert={inventoryStats.outOfStockProducts > 0}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Customers ===== */}
      <SectionHead icon="🧑‍🤝‍🧑" title="Customers" hint="Who is buying" />

      <Row className="g-3 mb-5">
        <Col xs={6} md={3}>
          <StatTile
            icon="👥"
            value={customerStats.totalCustomers}
            label="Total Customers"
            tone="violet"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="🆕"
            value={customerStats.newCustomers}
            label="New This Month"
            tone="green"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="🏆"
            value={customerStats.topBuyer}
            label="Top Buyer"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            icon="💰"
            value={customerStats.averageOrderValue}
            label="Avg Order Value"
            tone="cyan"
            prefix="₹"
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Tickets ===== */}
      <SectionHead
        icon="🎫"
        title="Support Tickets"
        hint={ticketStats.total > 0 ? `${ticketStats.total} total` : null}
      />

      <Row className="g-3 mb-5">
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🎫"
            value={ticketStats.total}
            label="Total Tickets"
            tone="slate"
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🔴"
            value={ticketStats.open}
            label="Open Tickets"
            tone="red"
            share={share(ticketStats.open, ticketStats.total)}
            alert={ticketStats.open > 0}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🟡"
            value={ticketStats.inProgress}
            label="In Progress"
            tone="amber"
            share={share(ticketStats.inProgress, ticketStats.total)}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🟢"
            value={ticketStats.closed}
            label="Closed Tickets"
            tone="green"
            share={share(ticketStats.closed, ticketStats.total)}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Refunds ===== */}
      <SectionHead
        icon="💸"
        title="Refunds"
        hint={refundTotal > 0 ? `${refundTotal} requests` : null}
      />

      <Row className="g-3 mb-5">
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🟡"
            value={refundStats.pending || 0}
            label="Pending Refunds"
            tone="amber"
            share={share(refundStats.pending, refundTotal)}
            alert={(refundStats.pending || 0) > 0}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🟢"
            value={refundStats.approved || 0}
            label="Approved Refunds"
            tone="green"
            share={share(refundStats.approved, refundTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🔵"
            value={refundStats.refunded || 0}
            label="Refunded"
            tone="blue"
            share={share(refundStats.refunded, refundTotal)}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🔴"
            value={refundStats.rejected || 0}
            label="Rejected Refunds"
            tone="red"
            share={share(refundStats.rejected, refundTotal)}
            loading={loading}
          />
        </Col>
      </Row>

      {/* ===== Reviews ===== */}
      <SectionHead
        icon="⭐"
        title="Reviews"
        hint="What customers think of your products"
      />

      <Row className="g-3 mb-4">
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="⭐"
            value={reviewStats.totalReviews}
            label="Total Reviews"
            tone="violet"
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🌟"
            value={Number(reviewStats.averageRating) || 0}
            label="Average Rating"
            tone="amber"
            decimals={1}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🟢"
            value={reviewStats.fiveStarReviews}
            label="5 Star Reviews"
            tone="green"
            share={share(reviewStats.fiveStarReviews, reviewStats.totalReviews)}
            loading={loading}
          />
        </Col>
        <Col xs={6} lg={3} md={6}>
          <StatTile
            icon="🔴"
            value={reviewStats.oneStarReviews}
            label="1 Star Reviews"
            tone="red"
            share={share(reviewStats.oneStarReviews, reviewStats.totalReviews)}
            alert={(reviewStats.oneStarReviews || 0) > 0}
            loading={loading}
          />
        </Col>
      </Row>
    </>
  );
}

const statCardStyles = `
  /* ===== Section headings ===== */
  .sc-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  /* Lamp medallion — breathes a gold ring, as if freshly lit */
  .sc-head-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    border-radius: 13px;
    background: linear-gradient(135deg, #fff7ed, #fde68a);
    border: 1px solid #f59e0b;
    animation: scLamp 3.2s ease-in-out infinite;
  }
  @keyframes scLamp {
    0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); }
    50%      { box-shadow: 0 0 0 9px rgba(245, 158, 11, 0); }
  }
  .sc-head-title {
    font-weight: 800;
    margin: 0;
    font-size: 1.05rem;
    color: #7c2d12;
  }
  .sc-head-hint {
    color: #a8a29e;
    font-size: 0.76rem;
  }
  /* Gold thread with a spark travelling along it */
  .sc-head-rule {
    position: relative;
    flex: 1 1 auto;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, #fbbf24, rgba(253, 230, 138, 0));
    margin-left: 6px;
  }
  .sc-head-rule::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -60px;
    width: 60px;
    height: 8px;
    border-radius: 8px;
    pointer-events: none;
    background: radial-gradient(circle, rgba(251, 191, 36, 0.95), transparent 70%);
    animation: scSpark 5s ease-in-out infinite;
  }
  @keyframes scSpark {
    0%       { left: -60px; opacity: 0; }
    12%      { opacity: 1; }
    70%      { opacity: 0.85; }
    100%     { left: 100%;   opacity: 0; }
  }

  /* ===== Stat tile ===== */
  .sc-tile {
    position: relative;
    overflow: hidden;
    border-radius: 18px !important;
    background: linear-gradient(160deg, #fffdf8, #fff8ee);
    border: 1px solid rgba(253, 230, 138, 0.7) !important;
    box-shadow: 0 8px 22px rgba(124, 45, 18, 0.08);
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  }
  .sc-tile::before {
    /* soft tinted wash that intensifies on hover */
    content: '';
    position: absolute;
    inset: 0;
    background: var(--soft);
    opacity: 0.55;
    transition: opacity 0.25s ease;
    pointer-events: none;
  }
  .sc-tile::after {
    /* incense-smoke blob drifting in the corner */
    content: '';
    position: absolute;
    top: -46px;
    right: -46px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--c1), var(--c2));
    opacity: 0.15;
    filter: blur(4px);
    transition: opacity 0.35s ease;
    pointer-events: none;
    animation: scDrift 9s ease-in-out infinite;
  }
  @keyframes scDrift {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%      { transform: translate(-16px, 14px) scale(1.2); }
  }
  /* Neighbouring tiles drift out of step so a row never pulses as one */
  .row > *:nth-child(2n) .sc-tile::after { animation-delay: -2.2s; }
  .row > *:nth-child(3n) .sc-tile::after { animation-delay: -4.5s; }
  .row > *:nth-child(4n) .sc-tile::after { animation-delay: -6.7s; }

  .sc-tile:hover {
    transform: translateY(-6px);
    border-color: rgba(245, 158, 11, 0.9) !important;
    box-shadow: 0 20px 38px rgba(124, 45, 18, 0.18);
  }
  .sc-tile:hover::before { opacity: 0.9; }
  .sc-tile:hover::after  { opacity: 0.24; }

  .sc-tile .card-body {
    position: relative;
    z-index: 1;
  }

  /* Coloured strip down the left edge — molten gold runs through it */
  .sc-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    z-index: 2;
    background: linear-gradient(180deg, var(--c1), #fbbf24 42%, var(--c2), var(--c1));
    background-size: 100% 260%;
    animation: scAccentFlow 5.5s linear infinite;
  }
  @keyframes scAccentFlow {
    from { background-position: 0 0; }
    to   { background-position: 0 -260%; }
  }

  .sc-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    font-size: 1.35rem;
    line-height: 1;
    border-radius: 14px;
    background: linear-gradient(140deg, #ffffff, #fff7ed);
    border: 1px solid #fde68a;
    box-shadow: 0 4px 12px rgba(180, 83, 9, 0.14);
    transition: transform 0.25s ease;
  }
  /* A blessing ring opens out of each icon */
  .sc-icon::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 17px;
    border: 1px solid rgba(245, 158, 11, 0.75);
    pointer-events: none;
    animation: scHalo 3.6s ease-out infinite;
  }
  @keyframes scHalo {
    0%        { transform: scale(0.82); opacity: 0.9; }
    65%, 100% { transform: scale(1.35); opacity: 0; }
  }
  .row > *:nth-child(2n) .sc-icon::after { animation-delay: -0.9s; }
  .row > *:nth-child(3n) .sc-icon::after { animation-delay: -1.8s; }
  .row > *:nth-child(4n) .sc-icon::after { animation-delay: -2.7s; }

  /* Proportion bar sits on a warm track */
  .sc-bar { background: rgba(180, 83, 9, 0.12) !important; }
  .sc-tile:hover .sc-icon {
    transform: scale(1.1) rotate(-6deg);
  }

  .sc-value {
    margin-top: 12px;
    font-weight: 800;
    font-size: clamp(1.25rem, 2.4vw, 1.85rem);
    line-height: 1.15;
    letter-spacing: -0.5px;
    word-break: break-word;
    overflow-wrap: anywhere;
    background: linear-gradient(120deg, var(--c1), var(--c2));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .sc-label {
    margin-top: 2px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #78716c;
  }

  /* Proportion bar */
  .sc-bar {
    margin-top: 10px;
    height: 6px;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.08);
    overflow: hidden;
  }
  .sc-bar-fill {
    display: block;
    height: 100%;
    border-radius: 6px;
    background: linear-gradient(90deg, var(--c1), var(--c2));
    transition: width 0.9s cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* Attention flag */
  .sc-tile-alert::before { opacity: 0.85; }
  .sc-flag {
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #b91c1c;
    background: #fff;
    border: 1px solid #fecaca;
    border-radius: 20px;
    padding: 3px 8px;
    white-space: nowrap;
    animation: scPulse 2.4s ease-in-out infinite;
  }
  @keyframes scPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.35); }
    50%      { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
  }

  /* Loading skeletons */
  .sc-tile-skel {
    background: #fff;
    box-shadow: 0 6px 18px rgba(124, 45, 18, 0.06);
  }
  .sc-tile-skel:hover { transform: none; }
  .sc-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: scShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .sc-skel-icon  { width: 44px; height: 44px; border-radius: 14px; }
  .sc-skel-value { width: 70%; height: 26px; margin-top: 14px; }
  .sc-skel-label { width: 50%; height: 11px; margin-top: 10px; }
  @keyframes scShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 575.98px) {
    .sc-tile .card-body { padding: 0.85rem !important; }
    .sc-icon { width: 36px; height: 36px; font-size: 1.05rem; border-radius: 11px; }
    .sc-value { font-size: 1.1rem; margin-top: 9px; }
    .sc-label { font-size: 0.7rem; }
    .sc-flag { display: none; }
    .sc-head-icon { width: 34px; height: 34px; font-size: 1rem; }
    .sc-head-title { font-size: 0.95rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .sc-tile, .sc-tile::before, .sc-tile::after, .sc-icon,
    .sc-icon::after, .sc-accent, .sc-head-icon, .sc-head-rule::after,
    .sc-bar-fill, .sc-flag, .sc-skel {
      transition: none;
      animation: none;
    }
    .sc-icon::after, .sc-head-rule::after { opacity: 0; }
  }
`;

export default StatsCards;
